import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useFinanceStore } from "../stores/finance-store";
import { getAccountStatementBalanceValue, getAccountStatementOpeningBalanceForMonth, getSignedTransactionAmount } from "../lib/finance-calculations";
import { findCategoryById, findUniqueCategoryByName } from "../lib/category-relations";
import { transactionMatchesAccount } from "../lib/account-relations";
import { normalizeCategoryName, normalizeUppercase, parseCurrency, roundMoney, stringToColor } from "../lib/utils";
import { CATEGORY_TYPES, TRANSACTION_KINDS } from "../lib/transaction-types";

function createDefaultAccountForm() {
  return {
    name: "",
    type: "checking",
    nature: "D",
    bank: "",
    bankIconId: null,
    currency: "BRL",
    initialBalance: "",
    active: true,
    showOnHome: true,
    showOnDashboard: true,
  };
}

function stripTransportFields(data = {}) {
  const {
    id,
    user_id,
    tenant_id,
    type,
    encrypted_payload,
    iv,
    created_at,
    updated_at,
    deleted_at,
    ...payload
  } = data;
  return payload;
}

function buildAccountPayload(formData, editId, existingAccount) {
  return {
    name: normalizeUppercase(formData.name),
    type: formData.type,
    nature: formData.nature || "D",
    bank: normalizeUppercase(formData.bank),
    bankIconId: formData.bankIconId || null,
    currency: formData.currency || "BRL",
    initialBalance: editId ? parseCurrency(existingAccount?.initialBalance) : parseCurrency(formData.initialBalance),
    active: formData.active !== false,
    showOnHome: formData.showOnHome !== false,
    showOnDashboard: formData.showOnDashboard !== false,
  };
}

async function syncLinkedCardsForAccount(cards, editId, accountPayload, updateCard) {
  const linkedCards = cards.filter((card) => card.accountId === editId);
  for (const card of linkedCards) {
    const cardResult = await updateCard(card.id, {
      ...stripTransportFields(card),
      accountName: accountPayload.name,
    });
    if (cardResult?.success === false) {
      return false;
    }
  }
  return true;
}

async function applyBalanceAdjustmentOnEdit({
  existingAccount,
  accountPayload,
  editId,
  targetCurrentBalance,
  transactions,
  accounts,
  month,
  year,
  createHiddenBalanceAdjustment,
}) {
  const balanceAfterAccountUpdate = getAccountStatementBalanceValue(
    { ...existingAccount, ...accountPayload },
    transactions,
    true,
    { accounts, month, year }
  );
  return createHiddenBalanceAdjustment(
    { ...existingAccount, ...accountPayload, id: editId },
    targetCurrentBalance,
    balanceAfterAccountUpdate
  );
}

async function resolveTransferCategory(transferCategories, categoryId, categoryName, addCategory) {
  let selectedCategory = findCategoryById(transferCategories, categoryId, CATEGORY_TYPES.TRANSFER) ||
    findUniqueCategoryByName(transferCategories, categoryName, CATEGORY_TYPES.TRANSFER);
  const categoryExists = transferCategories.some((item) => normalizeCategoryName(item.name) === categoryName);

  if (categoryName && !selectedCategory && !categoryExists) {
    const categoryRes = await addCategory(CATEGORY_TYPES.TRANSFER, { name: categoryName, color: stringToColor(categoryName) });
    if (categoryRes?.success === false) {
      return { error: true };
    }
    selectedCategory = categoryRes.category || null;
  }

  return { category: selectedCategory };
}

function buildTransferLegPayload({ description, amount, categoryName, categoryId, ownerAccount, fromAccount, toAccount, date, transferId, role }) {
  return {
    description,
    amount,
    category: categoryName || null,
    category_id: categoryId || null,
    account_id: ownerAccount.id,
    account: ownerAccount.name,
    date,
    status: true,
    confirmed: true,
    transaction_kind: TRANSACTION_KINDS.TRANSFER,
    linked_transfer_id: transferId,
    transfer_role: role,
    from_account: fromAccount.name,
    to_account: toAccount.name,
    from_account_id: fromAccount.id,
    to_account_id: toAccount.id,
    transfer_description: "Transferência",
  };
}

export function useAccountBalances(accounts, transactions) {
  const accountBalanceDate = new Date();
  const accountBalanceMonth = accountBalanceDate.getMonth();
  const accountBalanceYear = accountBalanceDate.getFullYear();

  const accountBalances = useMemo(() => {
    const map = {};
    accounts.forEach((account) => {
      map[account.id] = getAccountStatementBalanceValue(account, transactions, true, {
        accounts,
        month: accountBalanceMonth,
        year: accountBalanceYear,
      });
    });
    return map;
  }, [accounts, transactions, accountBalanceMonth, accountBalanceYear]);

  const totalBalance = useMemo(() => {
    return accounts.reduce((acc, account) => acc + (accountBalances[account.id] || 0), 0);
  }, [accounts, accountBalances]);

  const getAccountBalance = (account) => accountBalances[typeof account === "string" ? account : account?.id] || 0;

  return { accountBalances, totalBalance, getAccountBalance, accountBalanceMonth, accountBalanceYear };
}

export function useBalanceAdjustmentDialog() {
  const { addTransaction } = useFinanceStore();
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [adjustmentAccount, setAdjustmentAccount] = useState(null);

  const handleOpenAdjustment = (account) => {
    setAdjustmentAccount(account);
    setShowAdjustmentDialog(true);
  };

  const handleAdjustmentOpenChange = (open) => {
    setShowAdjustmentDialog(open);
    if (!open) setAdjustmentAccount(null);
  };

  const createHiddenBalanceAdjustment = async (account, targetBalance, currentBalance, options = {}) => {
    const diff = targetBalance - currentBalance;

    if (Math.abs(diff) < 0.00001) {
      return { success: true, skipped: true };
    }

    const type = diff > 0 ? "income" : "expense";
    const adjustmentDate = options.date || new Date().toISOString();
    return addTransaction(type, {
      description: "Ajuste de Saldo",
      amount: Math.abs(diff),
      category: "AJUSTE",
      account_id: account.id,
      account: account.name,
      date: adjustmentDate,
      realized_at: options.realizedAt || adjustmentDate,
      status: true,
      confirmed: true,
      transaction_kind: TRANSACTION_KINDS.BALANCE_ADJUSTMENT,
      hidden: true,
      affects_reports: false,
    });
  };

  return {
    showAdjustmentDialog,
    setShowAdjustmentDialog,
    adjustmentAccount,
    handleOpenAdjustment,
    handleAdjustmentOpenChange,
    createHiddenBalanceAdjustment,
  };
}

export function useStatementImportDialog() {
  const { accounts, transactions, addTransactionBatch, parseAccountStatementFile } = useFinanceStore();
  const [showStatementImportDialog, setShowStatementImportDialog] = useState(false);
  const [statementImportAccount, setStatementImportAccount] = useState(null);

  const statementAccountTransactions = useMemo(() => (
    statementImportAccount
      ? transactions.filter((tx) => transactionMatchesAccount(tx, statementImportAccount, accounts))
      : []
  ), [transactions, statementImportAccount, accounts]);

  const handleOpenStatementImport = (account) => {
    setStatementImportAccount(account);
    setShowStatementImportDialog(true);
  };

  const handleStatementImportOpenChange = (open) => {
    setShowStatementImportDialog(open);
    if (!open) setStatementImportAccount(null);
  };

  const handleImportStatementItems = async (items) => {
    const account = statementImportAccount;
    if (!account || !items.length) return { success: false };

    const batch = items.map((item) => ({
      type: item.direction === "credit" ? "income" : "expense",
      data: {
        description: normalizeUppercase(item.description),
        amount: parseCurrency(item.amount),
        category: null,
        category_id: null,
        account_id: account.id,
        account: account.name,
        date: new Date(`${item.date}T12:00:00`).toISOString(),
        status: true,
        confirmed: true,
        statement_fitid: item.fitid || null,
        statement_import: true,
      },
    }));

    const result = await addTransactionBatch(batch);
    if (result.success) {
      toast.success(`${batch.length} transaç${batch.length > 1 ? "ões importadas" : "ão importada"}!`);
      return { success: true };
    }

    toast.error("Erro ao importar extrato.");
    return { success: false };
  };

  return {
    showStatementImportDialog,
    statementImportAccount,
    statementAccountTransactions,
    handleOpenStatementImport,
    handleStatementImportOpenChange,
    handleImportStatementItems,
    parseAccountStatementFile,
  };
}

export function useAccountFormDialog(accountBalanceMonth, accountBalanceYear, createHiddenBalanceAdjustment, getAccountBalance) {
  const { accounts, cards, transactions, addAccount, updateAccount, updateCard } = useFinanceStore();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(createDefaultAccountForm);

  const handleOpenEdit = (account) => {
    setEditId(account.id);
    setFormData({
      name: normalizeUppercase(account.name),
      type: account.type || "checking",
      nature: account.nature || "D",
      bank: normalizeUppercase(account.bank),
      bankIconId: account.bankIconId || null,
      currency: account.currency || "BRL",
      initialBalance: String(getAccountBalance(account)),
      active: account.active !== false,
      showOnHome: account.showOnHome !== false,
      showOnDashboard: account.showOnDashboard !== false,
    });
    setShowAddDialog(true);
  };

  const openCreateAccountDialog = () => {
    setEditId(null);
    setFormData(createDefaultAccountForm());
    setShowAddDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const existingAccount = editId ? accounts.find((account) => account.id === editId) : null;
    const targetCurrentBalance = parseCurrency(formData.initialBalance);
    const accountPayload = buildAccountPayload(formData, editId, existingAccount);

    const result = editId ? await updateAccount(editId, accountPayload) : await addAccount(accountPayload);
    if (!result.success) {
      toast.error(editId ? "Erro ao atualizar conta" : "Erro ao adicionar conta");
      return;
    }

    if (editId) {
      const cardsSynced = await syncLinkedCardsForAccount(cards, editId, accountPayload, updateCard);
      if (!cardsSynced) {
        toast.error("Conta atualizada, mas o snapshot do cartão não foi atualizado.");
        return;
      }

      const adjustmentResult = await applyBalanceAdjustmentOnEdit({
        existingAccount,
        accountPayload,
        editId,
        targetCurrentBalance,
        transactions,
        accounts,
        month: accountBalanceMonth,
        year: accountBalanceYear,
        createHiddenBalanceAdjustment,
      });
      if (adjustmentResult?.success === false) {
        toast.error("Conta atualizada, mas o saldo não foi ajustado.");
        return;
      }
    }

    toast.success(editId ? "Conta atualizada!" : "Conta adicionada!");
    setFormData(createDefaultAccountForm());
    setEditId(null);
    setShowAddDialog(false);
  };

  return {
    showAddDialog,
    setShowAddDialog,
    editId,
    formData,
    setFormData,
    handleOpenEdit,
    openCreateAccountDialog,
    handleSubmit,
  };
}

export function useAccountTransferDialog() {
  const { accounts, categories, addTransaction, addCategory } = useFinanceStore();
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferData, setTransferData] = useState({
    fromAccountId: "",
    toAccountId: "",
    amount: "",
    category: "",
    categoryId: null,
  });

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    const amount = parseCurrency(transferData.amount);
    const categoryName = normalizeCategoryName(transferData.category);
    const fromAccount = accounts.find((account) => account.id === transferData.fromAccountId);
    const toAccount = accounts.find((account) => account.id === transferData.toAccountId);
    if (!amount || amount <= 0) return toast.error("Valor inválido");
    if (!fromAccount || !toAccount) return toast.error("Informe a conta origem e a conta destino.");
    if (fromAccount.id === toAccount.id) return toast.error("Contas iguais");

    const transferCategories = categories.filter((item) => item.type === CATEGORY_TYPES.TRANSFER);
    const categoryResult = await resolveTransferCategory(transferCategories, transferData.categoryId, categoryName, addCategory);
    if (categoryResult.error) return toast.error("Erro ao adicionar categoria");

    const transferId = crypto.randomUUID();
    const date = new Date().toISOString();
    const categoryFields = {
      categoryName: categoryResult.category?.name || categoryName || null,
      categoryId: categoryResult.category?.id || null,
    };

    const expenseRes = await addTransaction("expense", buildTransferLegPayload({
      description: `Transferência para ${toAccount.name}`,
      amount,
      ...categoryFields,
      ownerAccount: fromAccount,
      fromAccount,
      toAccount,
      date,
      transferId,
      role: "origin",
    }));
    const incomeRes = await addTransaction("income", buildTransferLegPayload({
      description: `Transferência de ${fromAccount.name}`,
      amount,
      ...categoryFields,
      ownerAccount: toAccount,
      fromAccount,
      toAccount,
      date,
      transferId,
      role: "destination",
    }));

    if (expenseRes.success && incomeRes.success) {
      toast.success("Transferência realizada!");
      setShowTransferDialog(false);
      setTransferData({ fromAccountId: "", toAccountId: "", amount: "", category: "", categoryId: null });
    } else {
      toast.error("Erro ao realizar transferência");
    }
  };

  return {
    showTransferDialog,
    setShowTransferDialog,
    transferData,
    setTransferData,
    handleTransferSubmit,
    categories,
  };
}

function computeAdjustmentSummary({
  account,
  accounts,
  transactions,
  currentBalance,
  parsedTargetBalance,
  isInitialAdjustment,
  today,
  currentMonth,
  currentYear,
}) {
  if (!account) return null;

  if (!isInitialAdjustment) {
    const diff = roundMoney(parsedTargetBalance - currentBalance);
    return {
      diff,
      targetBalance: parsedTargetBalance,
      currentBalance,
      adjustmentDate: today,
    };
  }

  const monthStart = new Date(currentYear, currentMonth, 1, 0, 0, 0, 0);
  const monthEnd = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59, 999);

  const accountScope = accounts.length ? accounts : account ? [account] : [];
  const openingBalance = getAccountStatementOpeningBalanceForMonth(account, transactions, currentMonth, currentYear, { accounts: accountScope });
  const reconciledTransactions = transactions
    .filter((tx) => transactionMatchesAccount(tx, account, accountScope) && tx.status === true)
    .filter((tx) => {
      const operationDate = new Date(tx.date || 0);
      return operationDate >= monthStart && operationDate <= monthEnd;
    })
    .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  const reconciledMonthTotal = reconciledTransactions
    .reduce((acc, tx) => acc + getSignedTransactionAmount(tx), 0);

  const targetOpeningBalance = roundMoney(parsedTargetBalance - reconciledMonthTotal);
  const diff = roundMoney(targetOpeningBalance - openingBalance);
  const adjustmentDate = new Date(monthStart);
  adjustmentDate.setMilliseconds(-1);

  return {
    diff,
    targetBalance: parsedTargetBalance,
    currentBalance,
    openingBalance: roundMoney(openingBalance),
    reconciledTransactions,
    reconciledMonthTotal: roundMoney(reconciledMonthTotal),
    targetOpeningBalance,
    adjustmentDate,
  };
}

function getAdjustmentCallArgs(isInitialAdjustment, adjustmentSummary) {
  return isInitialAdjustment
    ? [adjustmentSummary.targetOpeningBalance, adjustmentSummary.openingBalance]
    : [adjustmentSummary.targetBalance, adjustmentSummary.currentBalance];
}

async function runAdjustmentConfirmation({ account, adjustmentSummary, isInitialAdjustment, onCreateAdjustment, setIsSubmitting, closeDialog }) {
  if (!account || !adjustmentSummary) return;

  if (Math.abs(adjustmentSummary.diff) < 0.00001) {
    toast.info("Saldo já está correto");
    closeDialog();
    return;
  }

  setIsSubmitting(true);
  const [target, current] = getAdjustmentCallArgs(isInitialAdjustment, adjustmentSummary);
  const options = {
    date: adjustmentSummary.adjustmentDate.toISOString(),
    realizedAt: adjustmentSummary.adjustmentDate.toISOString(),
  };
  const result = await onCreateAdjustment(account, target, current, options);
  setIsSubmitting(false);

  if (result?.skipped) {
    toast.info("Saldo já está correto");
    closeDialog();
    return;
  }

  if (result?.success) {
    toast.success(isInitialAdjustment ? "Saldo inicial ajustado!" : "Saldo ajustado!");
    closeDialog();
  } else {
    toast.error("Erro ao ajustar saldo");
  }
}

export function useBalanceAdjustmentWizard(open, account, accounts, transactions, currentBalance, onCreateAdjustment) {
  const [step, setStep] = useState(1);
  const [adjustmentType, setAdjustmentType] = useState("balance");
  const [targetBalance, setTargetBalance] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referenceDate, setReferenceDate] = useState(() => new Date());
  const [showReconciledItems, setShowReconciledItems] = useState(false);
  const targetBalanceInputRef = useRef(null);

  useEffect(() => {
    if (!open || !account) return;

    setStep(1);
    setAdjustmentType("balance");
    setTargetBalance("");
    setIsSubmitting(false);
    setShowReconciledItems(false);
    setReferenceDate(new Date());
  }, [open, account]);

  useEffect(() => {
    if (open && step === 2) {
      window.setTimeout(() => targetBalanceInputRef.current?.focus(), 0);
    }
  }, [open, step]);

  const today = referenceDate;
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const monthName = today.toLocaleString("pt-BR", { month: "long", year: "numeric" });
  const currency = account?.currency || "BRL";
  const parsedTargetBalance = parseCurrency(targetBalance);
  const isInitialAdjustment = adjustmentType === "opening";

  const adjustmentSummary = useMemo(() => computeAdjustmentSummary({
    account, accounts, transactions, currentBalance, parsedTargetBalance, isInitialAdjustment, today, currentMonth, currentYear,
  }), [account, accounts, transactions, currentBalance, parsedTargetBalance, isInitialAdjustment, today, currentMonth, currentYear]);

  const handleNext = () => {
    if (step === 2 && targetBalance.trim() === "") {
      toast.error("Informe o saldo atual da conta");
      targetBalanceInputRef.current?.focus();
      return;
    }
    if (step < 3) setStep((current) => current + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep((current) => current - 1);
  };

  const handleConfirm = (closeDialog) => runAdjustmentConfirmation({
    account, adjustmentSummary, isInitialAdjustment, onCreateAdjustment, setIsSubmitting, closeDialog,
  });

  return {
    step,
    adjustmentType,
    setAdjustmentType,
    targetBalance,
    setTargetBalance,
    isSubmitting,
    targetBalanceInputRef,
    monthName,
    currency,
    isInitialAdjustment,
    adjustmentSummary,
    showReconciledItems,
    setShowReconciledItems,
    handleNext,
    handleBack,
    handleConfirm,
  };
}
